/**
 * UAPC Anytime - Register Page
 * @module RegisterPage
 * @author Mohammed Qamrain Shaikh - Advanced Full Stack Developer
 * @description User registration for vendors and customers
 * @version 1.0.0
 * Git: Mohammed Qamrain Shaikh <mohammed@linesoft.com> - Registration flow
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, UserPlus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    role: 'vendor',
    company_name: '',
    phone: '',
    address: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...userData } = formData;
      const newUser = await register(userData);
      
      if (newUser.role === 'vendor' && !newUser.is_approved) {
        toast.success('Registration successful! Your account is pending approval.');
        navigate('/login');
      } else {
        toast.success('Registration successful!');
        navigate(newUser.role === 'admin' ? '/admin' : '/vendor');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Left Panel - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative"
        style={{
          backgroundImage: 'linear-gradient(to bottom, rgba(15, 23, 42, 0.7), rgba(15, 23, 42, 0.9)), url(https://images.unsplash.com/photo-1743314777689-1bb71ae148ca?q=80&w=2070&auto=format&fit=crop)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 flex flex-col justify-center px-12">
          <Link to="/" className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-blue-600 flex items-center justify-center">
              <span className="font-heading font-black text-2xl text-white">U</span>
            </div>
            <span className="font-heading font-bold text-2xl text-white tracking-tight">UAPC ANYTIME</span>
          </Link>
          <h1 className="font-heading font-black text-5xl text-white uppercase tracking-tighter">
            Join Our<br />Marketplace
          </h1>
          <p className="mt-4 text-slate-300 text-lg max-w-md">
            Register as a vendor to list your inventory and reach thousands of customers in Houston.
          </p>
          <div className="mt-8 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-blue-500" />
              <span className="text-slate-300">Real-time inventory sync</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-blue-500" />
              <span className="text-slate-300">Secure payment processing</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-blue-500" />
              <span className="text-slate-300">Detailed analytics & reports</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white dark:bg-slate-900 overflow-y-auto">
        <div className="w-full max-w-md py-8">
          {/* Mobile Logo */}
          <Link to="/" className="flex items-center gap-2 mb-8 lg:hidden">
            <div className="w-10 h-10 bg-blue-600 flex items-center justify-center">
              <span className="font-heading font-black text-xl text-white">U</span>
            </div>
            <span className="font-heading font-bold text-xl text-slate-900 dark:text-white">UAPC ANYTIME</span>
          </Link>

          <h2 className="font-heading font-bold text-3xl text-slate-900 dark:text-white uppercase mb-2">
            Create Account
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mb-8">
            Fill in your details to get started
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="role" className="text-slate-900 dark:text-white font-medium">
                Account Type
              </Label>
              <Select 
                value={formData.role} 
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger className="h-12 bg-slate-50 dark:bg-slate-800" data-testid="register-role">
                  <SelectValue placeholder="Select account type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vendor">Vendor</SelectItem>
                  <SelectItem value="customer">Customer</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-900 dark:text-white font-medium">
                Full Name
              </Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="John Doe"
                className="h-12 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                required
                data-testid="register-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-900 dark:text-white font-medium">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="you@example.com"
                className="h-12 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                required
                data-testid="register-email"
              />
            </div>

            {formData.role === 'vendor' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="company" className="text-slate-900 dark:text-white font-medium">
                    Company Name
                  </Label>
                  <Input
                    id="company"
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    placeholder="Your Auto Parts Co."
                    className="h-12 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                    data-testid="register-company"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-slate-900 dark:text-white font-medium">
                    Phone Number
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    className="h-12 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                    data-testid="register-phone"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address" className="text-slate-900 dark:text-white font-medium">
                    Business Address
                  </Label>
                  <Input
                    id="address"
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="123 Main St, Houston, TX"
                    className="h-12 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                    data-testid="register-address"
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-900 dark:text-white font-medium">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  className="h-12 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 pr-10"
                  required
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-slate-900 dark:text-white font-medium">
                Confirm Password
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                placeholder="••••••••"
                className="h-12 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                required
                data-testid="register-confirm-password"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 font-heading font-bold uppercase tracking-wider"
              data-testid="register-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creating Account...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5" />
                  Create Account
                </span>
              )}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-slate-600 dark:text-slate-400">
              Already have an account?{' '}
              <Link 
                to="/login" 
                className="text-blue-600 hover:text-blue-700 font-bold"
                data-testid="login-link"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
